import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  closeAccountingPeriod,
  createAccountingPeriod,
  listAccountingPeriods,
  listChartOfAccounts,
  listJournalEntries,
  listJournalLines,
  postManualJournalEntry,
  reverseJournalEntry,
} from '../../api/finance';
import { usePermissions } from '../../context/PermissionContext';
import { useBranch } from '../../context/BranchContext';
import {
  Button,
  ClickableRow,
  Drawer,
  DrawerField,
  EmptyState,
  ErrorNote,
  FieldLabel,
  LoadingNote,
  PageHeader,
  Select,
  StatusBadge,
  SummaryStrip,
  TextInput,
  tableStyle,
  tdStyle,
  thStyle,
} from '../../components/common';
import type { AccountingPeriod, ChartOfAccount, JournalEntry, JournalLine } from '../../types/domain';

const currency = (n: number) => `Rp ${n.toLocaleString('id-ID')}`;

/**
 * Journals + accounting-period management (FASE 10, migration 0044).
 * Double-entry (sum debit = sum credit) is enforced server-side — this UI
 * never posts an unbalanced entry. A posted journal is never edited
 * directly; correction is always via reversal (fn_reverse_journal_entry).
 */
export default function JournalsPage() {
  const { hasPermission } = usePermissions();
  const { activeBranchId } = useBranch();
  const [periods, setPeriods] = useState<AccountingPeriod[]>([]);
  const [journals, setJournals] = useState<JournalEntry[]>([]);
  const [accounts, setAccounts] = useState<ChartOfAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPeriodForm, setShowPeriodForm] = useState(false);
  const [showJournalForm, setShowJournalForm] = useState(false);
  const [selected, setSelected] = useState<JournalEntry | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [closingId, setClosingId] = useState<string | null>(null);

  const canReadPeriod = hasPermission('accounting_period', 'read');
  const canCreatePeriod = hasPermission('accounting_period', 'create');
  const canClosePeriod = hasPermission('accounting_period', 'close');
  const canPost = hasPermission('journal_entry', 'post');
  const canReverse = hasPermission('journal_entry', 'reverse');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [periodRows, journalRows, coaRows] = await Promise.all([
        canReadPeriod ? listAccountingPeriods() : Promise.resolve([]),
        listJournalEntries(100, activeBranchId ?? undefined),
        listChartOfAccounts(),
      ]);
      setPeriods(periodRows);
      setJournals(journalRows);
      setAccounts(coaRows);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBranchId]);

  const openPeriods = useMemo(() => periods.filter((p) => p.status === 'OPEN'), [periods]);

  const handleClose = async (id: string) => {
    setActionErr(null);
    setClosingId(id);
    try {
      await closeAccountingPeriod(id);
      void load();
    } catch (e) {
      setActionErr(e instanceof Error ? e.message : String(e));
    } finally {
      setClosingId(null);
    }
  };

  const postedCount = journals.filter((j) => j.status === 'POSTED').length;
  const reversedCount = journals.filter((j) => j.status === 'REVERSED').length;

  return (
    <div>
      <PageHeader title="Jurnal & Periode Akuntansi" subtitle="Double-entry wajib seimbang — koreksi selalu lewat reversal, bukan edit langsung. Migrasi 0044." />

      <div className="alex-card" style={{ padding: 16, marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 'var(--alex-fs-caption)' }}>Periode Akuntansi</div>
          {canCreatePeriod && (
            <Button variant="secondary" onClick={() => setShowPeriodForm((v) => !v)}>
              {showPeriodForm ? 'Batal' : '+ Periode baru'}
            </Button>
          )}
        </div>
        {showPeriodForm && canCreatePeriod && (
          <NewPeriodForm
            onCreated={() => {
              setShowPeriodForm(false);
              void load();
            }}
          />
        )}
        {actionErr && <ErrorNote text={actionErr} />}
        {periods.length === 0 && !loading && <EmptyState text="Belum ada periode akuntansi." variant="compact" />}
        {periods.length > 0 && (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Kode</th>
                <th style={thStyle}>Mulai</th>
                <th style={thStyle}>Selesai</th>
                <th style={thStyle}>Status</th>
                {canClosePeriod && <th style={thStyle}></th>}
              </tr>
            </thead>
            <tbody>
              {periods.map((p) => (
                <tr key={p.period_id}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{p.period_code}</td>
                  <td style={tdStyle}>{p.start_date}</td>
                  <td style={tdStyle}>{p.end_date}</td>
                  <td style={tdStyle}>
                    <StatusBadge status={p.status} />
                  </td>
                  {canClosePeriod && (
                    <td style={tdStyle}>
                      {p.status === 'OPEN' && (
                        <Button variant="secondary" loading={closingId === p.period_id} onClick={() => void handleClose(p.period_id)}>
                          Tutup Periode
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {loading && <LoadingNote />}
      {error && <ErrorNote text={error} />}
      {!loading && !error && (
        <>
          <SummaryStrip
            items={[
              { label: 'Total Jurnal', value: journals.length },
              { label: 'Terposting', value: postedCount, tone: 'success' as const },
              { label: 'Dibalik (Reversed)', value: reversedCount, tone: 'neutral' as const },
            ]}
          />
          {canPost && (
            <Button variant="primary" onClick={() => setShowJournalForm((v) => !v)} style={{ marginBottom: 16 }} disabled={!activeBranchId || openPeriods.length === 0}>
              {showJournalForm ? 'Batal' : '+ Jurnal Manual'}
            </Button>
          )}
          {showJournalForm && canPost && activeBranchId && (
            <NewManualJournalForm
              branchId={activeBranchId}
              periods={openPeriods}
              accounts={accounts}
              onCreated={() => {
                setShowJournalForm(false);
                void load();
              }}
            />
          )}
          {journals.length === 0 && <EmptyState text="Belum ada jurnal." />}
          {journals.length > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Tanggal</th>
                  <th style={thStyle}>No. Jurnal</th>
                  <th style={thStyle}>Deskripsi</th>
                  <th style={thStyle}>Sumber</th>
                  <th style={thStyle}>Status</th>
                </tr>
              </thead>
              <tbody>
                {journals.map((je) => (
                  <ClickableRow key={je.journal_id} onClick={() => setSelected(je)}>
                    <td style={tdStyle}>{je.entry_date}</td>
                    <td style={{ ...tdStyle, color: 'var(--alex-primary)', fontWeight: 600 }}>{je.journal_no}</td>
                    <td style={tdStyle}>{je.description ?? '—'}</td>
                    <td style={tdStyle}>{je.source_type}</td>
                    <td style={tdStyle}>
                      <StatusBadge status={je.status} />
                    </td>
                  </ClickableRow>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      <JournalDetailDrawer
        journal={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
        accounts={accounts}
        periods={openPeriods}
        canReverse={canReverse}
        onChanged={() => void load()}
      />
    </div>
  );
}

function NewPeriodForm({ onCreated }: { onCreated: () => void }) {
  const [periodCode, setPeriodCode] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await createAccountingPeriod(periodCode.trim(), startDate, endDate);
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ marginBottom: 14, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <div style={{ width: 140 }}>
        <FieldLabel label="Kode Periode">
          <TextInput value={periodCode} onChange={(e) => setPeriodCode(e.target.value)} required autoFocus placeholder="2026-08" />
        </FieldLabel>
      </div>
      <div style={{ width: 150 }}>
        <FieldLabel label="Mulai">
          <TextInput type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} required />
        </FieldLabel>
      </div>
      <div style={{ width: 150 }}>
        <FieldLabel label="Selesai">
          <TextInput type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} required />
        </FieldLabel>
      </div>
      <Button type="submit" variant="primary" loading={submitting}>
        Simpan
      </Button>
      {error && <ErrorNote text={error} />}
    </form>
  );
}

function NewManualJournalForm({
  branchId,
  periods,
  accounts,
  onCreated,
}: {
  branchId: string;
  periods: AccountingPeriod[];
  accounts: ChartOfAccount[];
  onCreated: () => void;
}) {
  const [periodId, setPeriodId] = useState('');
  const [entryDate, setEntryDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState('');
  const [debitAccountId, setDebitAccountId] = useState('');
  const [creditAccountId, setCreditAccountId] = useState('');
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const postableAccounts = accounts.filter((a) => a.is_postable && a.status === 'ACTIVE');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      setError('Jumlah harus lebih dari 0.');
      return;
    }
    if (debitAccountId === creditAccountId) {
      setError('Akun debit dan kredit harus berbeda.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await postManualJournalEntry(branchId, periodId, entryDate, description, [
        { account_id: debitAccountId, debit: amt, credit: 0 },
        { account_id: creditAccountId, debit: 0, credit: amt },
      ]);
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="alex-card" style={{ marginBottom: 20, padding: 16, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <div style={{ width: 160 }}>
        <FieldLabel label="Periode (OPEN)">
          <Select value={periodId} onChange={(e) => setPeriodId(e.target.value)} required>
            <option value="" disabled>
              Pilih...
            </option>
            {periods.map((p) => (
              <option key={p.period_id} value={p.period_id}>
                {p.period_code}
              </option>
            ))}
          </Select>
        </FieldLabel>
      </div>
      <div style={{ width: 150 }}>
        <FieldLabel label="Tanggal">
          <TextInput type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} required />
        </FieldLabel>
      </div>
      <div style={{ flex: 1, minWidth: 200 }}>
        <FieldLabel label="Deskripsi">
          <TextInput value={description} onChange={(e) => setDescription(e.target.value)} required />
        </FieldLabel>
      </div>
      <div style={{ width: 190 }}>
        <FieldLabel label="Akun Debit">
          <Select value={debitAccountId} onChange={(e) => setDebitAccountId(e.target.value)} required>
            <option value="" disabled>
              Pilih...
            </option>
            {postableAccounts.map((a) => (
              <option key={a.account_id} value={a.account_id}>
                {a.account_code} — {a.name}
              </option>
            ))}
          </Select>
        </FieldLabel>
      </div>
      <div style={{ width: 190 }}>
        <FieldLabel label="Akun Kredit">
          <Select value={creditAccountId} onChange={(e) => setCreditAccountId(e.target.value)} required>
            <option value="" disabled>
              Pilih...
            </option>
            {postableAccounts.map((a) => (
              <option key={a.account_id} value={a.account_id}>
                {a.account_code} — {a.name}
              </option>
            ))}
          </Select>
        </FieldLabel>
      </div>
      <div style={{ width: 160 }}>
        <FieldLabel label="Jumlah (Rp)">
          <TextInput type="number" min="0" step="1" value={amount} onChange={(e) => setAmount(e.target.value)} required />
        </FieldLabel>
      </div>
      <Button type="submit" variant="primary" loading={submitting}>
        Posting
      </Button>
      <div style={{ width: '100%', fontSize: 'var(--alex-fs-micro)', color: 'var(--alex-text-faint)' }}>
        Debit dan kredit otomatis seimbang (dua sisi entry yang sama) — server menolak jurnal yang tidak seimbang.
      </div>
      {error && <ErrorNote text={error} />}
    </form>
  );
}

function JournalDetailDrawer({
  journal,
  open,
  onClose,
  accounts,
  periods,
  canReverse,
  onChanged,
}: {
  journal: JournalEntry | null;
  open: boolean;
  onClose: () => void;
  accounts: ChartOfAccount[];
  periods: AccountingPeriod[];
  canReverse: boolean;
  onChanged: () => void;
}) {
  const [lines, setLines] = useState<JournalLine[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [showReverseForm, setShowReverseForm] = useState(false);
  const [reversePeriodId, setReversePeriodId] = useState('');
  const [reverseDate, setReverseDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLines(null);
    setLoadErr(null);
    setShowReverseForm(false);
    setReason('');
    setError(null);
    if (!journal) return;
    listJournalLines([journal.journal_id]).then(setLines).catch((e) => setLoadErr(e instanceof Error ? e.message : String(e)));
  }, [journal?.journal_id]);

  if (!journal) return null;

  const accountLabel = (id: string) => {
    const a = accounts.find((x) => x.account_id === id);
    return a ? `${a.account_code} — ${a.name}` : id;
  };
  const totalDebit = lines?.reduce((s, l) => s + l.debit, 0) ?? 0;
  const totalCredit = lines?.reduce((s, l) => s + l.credit, 0) ?? 0;

  const handleReverse = async () => {
    if (!reason.trim()) {
      setError('Alasan pembalikan wajib diisi.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await reverseJournalEntry(journal.journal_id, reversePeriodId, reverseDate, reason.trim());
      onChanged();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer open={open} onClose={onClose} title={`Jurnal ${journal.journal_no}`}>
      <DrawerField label="Status" value={<StatusBadge status={journal.status} />} />
      <DrawerField label="Tanggal" value={journal.entry_date} />
      <DrawerField label="Sumber" value={journal.source_type} />
      <DrawerField label="Deskripsi" value={journal.description ?? '—'} />

      <div style={{ marginTop: 14, fontSize: 'var(--alex-fs-caption)', fontWeight: 700, color: 'var(--alex-text-primary)', marginBottom: 6 }}>Baris Jurnal (Double-Entry)</div>
      {loadErr && <ErrorNote text={loadErr} />}
      {lines === null && <LoadingNote />}
      {lines && lines.length > 0 && (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Akun</th>
              <th style={thStyle}>Debit</th>
              <th style={thStyle}>Kredit</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.line_id}>
                <td style={tdStyle}>{accountLabel(l.account_id)}</td>
                <td style={tdStyle}>{l.debit > 0 ? currency(l.debit) : '—'}</td>
                <td style={tdStyle}>{l.credit > 0 ? currency(l.credit) : '—'}</td>
              </tr>
            ))}
            <tr>
              <td style={{ ...tdStyle, fontWeight: 700 }}>Total {totalDebit === totalCredit ? '(seimbang)' : '(TIDAK SEIMBANG — periksa data)'}</td>
              <td style={{ ...tdStyle, fontWeight: 700 }}>{currency(totalDebit)}</td>
              <td style={{ ...tdStyle, fontWeight: 700 }}>{currency(totalCredit)}</td>
            </tr>
          </tbody>
        </table>
      )}

      {journal.status === 'POSTED' && canReverse && (
        <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--alex-border)' }}>
          <Button variant="ghost" onClick={() => setShowReverseForm((v) => !v)}>
            {showReverseForm ? 'Batal' : 'Balik Jurnal (Reversal)'}
          </Button>
          {showReverseForm && (
            <div style={{ marginTop: 10 }}>
              <p style={{ fontSize: 'var(--alex-fs-micro)', color: 'var(--alex-text-faint)', margin: '0 0 10px' }}>
                Jurnal asli tidak pernah diedit — pembalikan membuat jurnal baru yang saling terkait (debit/kredit ditukar), lalu menandai jurnal asli sebagai REVERSED.
              </p>
              <FieldLabel label="Periode Akuntansi (OPEN)">
                <Select value={reversePeriodId} onChange={(e) => setReversePeriodId(e.target.value)}>
                  <option value="">Pilih...</option>
                  {periods.map((p) => (
                    <option key={p.period_id} value={p.period_id}>
                      {p.period_code}
                    </option>
                  ))}
                </Select>
              </FieldLabel>
              <div style={{ marginTop: 8 }}>
                <FieldLabel label="Tanggal Pembalikan">
                  <TextInput type="date" value={reverseDate} onChange={(e) => setReverseDate(e.target.value)} />
                </FieldLabel>
              </div>
              <div style={{ marginTop: 8 }}>
                <FieldLabel label="Alasan">
                  <TextInput value={reason} onChange={(e) => setReason(e.target.value)} placeholder="mis. salah akun, koreksi jumlah" />
                </FieldLabel>
              </div>
              <Button variant="primary" loading={submitting} style={{ marginTop: 10 }} onClick={() => void handleReverse()} disabled={!reversePeriodId}>
                Konfirmasi Pembalikan
              </Button>
              {error && <ErrorNote text={error} />}
            </div>
          )}
        </div>
      )}
      {journal.status === 'REVERSED' && (
        <p style={{ fontSize: 'var(--alex-fs-micro)', color: 'var(--alex-text-faint)', marginTop: 16 }}>Jurnal ini sudah dibalik — lihat jurnal reversal terkait di daftar jurnal.</p>
      )}
    </Drawer>
  );
}
